import { Tooltip, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import { Iconify } from 'src/components';

// WA-consistent blue for "read" ticks. Using the theme's cyan (info.main
// = #00B8D9 in this codebase) would read too teal next to the green sent
// bubble. #34B7F1 is already WA-native and stays consistent with the
// existing palette tokens — not a new colour introduction, just preserved.
const READ_BLUE = '#34B7F1';

// Why Meta refused a message, in words an operator can act on.
//
// The tooltip said "Failed to send" and nothing else, so the one thing the
// operator needed — is this my fault, the patient's, or Meta's? — was the one
// thing it withheld. Measured on practice 36: a re-engagement template came
// back 131049, which is Meta capping marketing templates per recipient. Nothing
// on the screen said so, and the natural read of a red icon is "the system is
// broken", which sends someone to check a WABA that is perfectly healthy.
const META_ERROR_EXPLANATIONS = {
    131026: 'Not delivered — this number is not a WhatsApp user, or cannot receive messages.',
    131047: 'Outside the 24-hour window, so only an approved template can be sent.',
    131048: 'Meta is rate-limiting this number for spam. Slow down and check the message.',
    131049: 'Meta withheld this to limit marketing messages to this patient. '
        + 'A utility-category template is not capped this way.',
    131042: 'Billing problem on the WhatsApp Business account. Check the payment method.',
    132000: 'The template was sent with the wrong number of values.',
    132001: 'That template no longer exists in this language.',
    132005: 'The filled-in template is too long, or was edited after approval.',
    132007: 'The template breaks WhatsApp content rules.',
    132012: 'A value does not match the format the template expects.',
    132015: 'That template is paused by Meta for low quality.',
    132016: 'That template is disabled by Meta for low quality.',
    133010: 'This number is not registered with the WhatsApp Cloud API.',
    368: 'This number is temporarily blocked by Meta for policy violations.',
};

const failureReason = (metaErrorCode, error) => {
    const known = META_ERROR_EXPLANATIONS[Number(metaErrorCode)];
    if (known) return known;
    // Unknown code: Meta's own text beats our silence, even though it reads
    // like a machine wrote it, because it at least names the cause.
    const detail = (error || '').trim();
    if (detail) return `Failed to send — ${detail}`;
    return 'Failed to send';
};

const StatusIcon = ({ status, updatedTimestamp, metaErrorCode, error }) => {
    if (status === 'sending' || status === 'queued') {
        return (
            <CircularProgress size={10} sx={{ ml: '0.3rem', color: 'text.disabled' }} />
        );
    }

    if (status === 'failed' || status === 'error') {
        return (
            <Tooltip placement="bottom" title={failureReason(metaErrorCode, error)}>
                <Iconify
                    icon="mdi:alert-circle"
                    sx={{
                        color: '#FF5630',
                        width: '1rem',
                        height: '1rem',
                        ml: '0.2rem',
                    }}
                />
            </Tooltip>
        );
    }

    const commonProps = {
        placement: 'bottom',
        title: updatedTimestamp,
    };

    if (status === 'read') {
        return (
            <Tooltip {...commonProps}>
                <Iconify
                    icon="charm:tick-double"
                    sx={{
                        color: READ_BLUE,
                        width: '1rem',
                        height: '1rem',
                        cursor: 'pointer',
                        ml: '0.2rem',
                    }}
                />
            </Tooltip>
        );
    }

    if (status === 'delivered') {
        return (
            <Tooltip {...commonProps}>
                <Iconify
                    icon="charm:tick-double"
                    sx={{
                        color: 'text.disabled',
                        width: '1rem',
                        height: '1rem',
                        ml: '0.2rem',
                    }}
                />
            </Tooltip>
        );
    }

    // sent / undefined / unknown → single grey tick
    return (
        <Tooltip {...commonProps}>
            <Iconify
                icon="charm:tick"
                sx={{
                    color: 'text.disabled',
                    width: '1rem',
                    height: '1rem',
                    ml: '0.2rem',
                }}
            />
        </Tooltip>
    );
};

StatusIcon.propTypes = {
    status: PropTypes.oneOf([
        'sending',
        'queued',
        'sent',
        'delivered',
        'read',
        'failed',
        'error',
    ]),
    updatedTimestamp: PropTypes.string,
    metaErrorCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    error: PropTypes.string,
};

StatusIcon.defaultProps = {
    status: undefined,
    updatedTimestamp: undefined,
    metaErrorCode: undefined,
    error: undefined,
};

export default StatusIcon;